import { DotLottie, DotLottieReact } from '@lottiefiles/dotlottie-react'
import { Box } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavTabs } from '../../../../components/navigation/NavTabs'
import { MISSIONS_TABS } from '../../../../components/navigation/tabs'
import { useStoreActions, useStoreState } from '../../../../store/hooks'
import { CounterOverlay } from './CounterOverlay'

type UpgradeStep = 'loading' | 'upgrading' | 'upgraded'

export const ClaimMissionAnimation = () => {
    const [dotLottie, setDotLottie] = useState<DotLottie>()
    const [upgradeArrowDotLottie, setUpgradeArrowDotLottie] = useState<DotLottie>()
    const [upgradedDotLottie, setUpgradedDotLottie] = useState<DotLottie>()
    const [showReward, setShowReward] = useState(false)
    const [upgradeStep, setUpgradeStep] = useState<UpgradeStep>('loading')
    const upgradingMission = useStoreState((state) => state.missionsService.upgradingMission)
    const claimedMission = useStoreState((state) => state.missionsService.claimedMission)
    const setClaimedMission = useStoreActions((state) => state.missionsService.setClaimedMission)
    const setUpgradingMission = useStoreActions((state) => state.missionsService.setUpgradingMission)
    const achievement = claimedMission?.achievements?.[0]
    const { changeTab } = useNavTabs()

    // Determine if this is a tier upgrade flow
    const isTierUpgrade = upgradingMission?.upgradesTier && upgradingMission?.previousAssetUrl

    // Handle tier upgrade step transitions
    useEffect(() => {
        if (isTierUpgrade && claimedMission) {
            setUpgradeStep('upgrading')
        }
    }, [isTierUpgrade, claimedMission])

    // Handle upgrade arrow animation frames
    useEffect(() => {
        if (!upgradeArrowDotLottie || !isTierUpgrade || upgradeStep !== 'upgrading') return

        const handleFrame = ({ currentFrame }: { currentFrame: number }) => {
            const totalFrames = upgradeArrowDotLottie.totalFrames
            if (totalFrames === 0) return

            // Reveal newAsset near the end of arrow animation (e.g., 80%)
            if (currentFrame >= totalFrames * 0.3) {
                setUpgradeStep('upgraded')
            }
        }

        upgradeArrowDotLottie.addEventListener('frame', handleFrame)

        return () => {
            upgradeArrowDotLottie.removeEventListener('frame', handleFrame)
        }
    }, [upgradeArrowDotLottie, isTierUpgrade, upgradeStep])

    // Handle upgraded animation completion and frame changes
    useEffect(() => {
        if (!upgradedDotLottie || !isTierUpgrade || upgradeStep !== 'upgraded') return

        const handleComplete = () => {
            setTimeout(() => {
                setClaimedMission(undefined)
                setUpgradingMission(undefined)
                changeTab(MISSIONS_TABS.ACHIEVEMENTS)
            }, 250)
        }

        const handleFrame = ({ currentFrame }: { currentFrame: number }) => {
            setShowReward(currentFrame > 8 && currentFrame < 85)
        }

        upgradedDotLottie.addEventListener('complete', handleComplete)
        upgradedDotLottie.addEventListener('frame', handleFrame)

        return () => {
            upgradedDotLottie.removeEventListener('complete', handleComplete)
            upgradedDotLottie.removeEventListener('frame', handleFrame)
        }
    }, [upgradedDotLottie, isTierUpgrade, upgradeStep, setClaimedMission, setUpgradingMission, changeTab])

    // Handle regular (non-tier-upgrade) animation
    useEffect(() => {
        if (!dotLottie || isTierUpgrade) return

        const handleComplete = () => {
            setTimeout(() => {
                setClaimedMission(undefined)
                setUpgradingMission(undefined)
                changeTab(MISSIONS_TABS.ACHIEVEMENTS)
            }, 250)
        }

        const handleFrame = ({ currentFrame }: { currentFrame: number }) => {
            setShowReward(currentFrame > 4 && currentFrame < 85)
        }

        dotLottie.addEventListener('complete', handleComplete)
        dotLottie.addEventListener('frame', handleFrame)

        return () => {
            dotLottie.removeEventListener('complete', handleComplete)
            dotLottie.removeEventListener('frame', handleFrame)
        }
    }, [dotLottie, isTierUpgrade, setClaimedMission, setUpgradingMission, changeTab])

    const dotLottieRefCallback = (dotLottie) => {
        setDotLottie(dotLottie)
    }

    const upgradeArrowDotLottieRefCallback = (dotLottie) => {
        setUpgradeArrowDotLottie(dotLottie)
    }

    const upgradedDotLottieRefCallback = (dotLottie) => {
        setUpgradedDotLottie(dotLottie)
    }

    // Check if we should show tier upgrade flow or regular flow
    if (isTierUpgrade) {
        return (
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.85)',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        maxWidth: '600px',
                        width: '100%',
                        aspectRatio: '1/1',
                        filter: 'drop-shadow(4px 10px 4px rgba(0, 0, 0, 0.75))',
                        position: 'relative',
                        '@keyframes scaleIn': {
                            '0%': { transform: 'scale(0.33)' },
                            '100%': { transform: 'scale(1)' },
                        },
                    }}
                >
                    {!showReward && upgradeStep !== 'upgraded' && (
                        <>
                            <Box
                                sx={{
                                    '@keyframes fadeIn': {
                                        '0%': { opacity: 0.1 },
                                        '100%': { opacity: 1 },
                                    },

                                    animation: 'fadeIn 1.2s ease-in-out forwards',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    zIndex: 1,
                                }}
                            >
                                <DotLottieReact
                                    src="/assets/animations/rewards/arrows-loop-loader.lottie"
                                    autoplay
                                    loop={true}
                                    layout={{ fit: 'contain', align: [0, 0] }}
                                    renderConfig={{
                                        autoResize: true,
                                    }}
                                />
                            </Box>
                            <svg
                                width="100%"
                                height="100%"
                                viewBox="0 0 557 572"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                preserveAspectRatio="xMidYMid meet"
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    zIndex: 2,
                                    animation: 'scaleIn 0.5s ease-in-out forwards',
                                }}
                            >
                                <image
                                    href={upgradingMission.previousAssetUrl}
                                    height="220"
                                    width="220"
                                    x={(557 - 220) / 2}
                                    y="190"
                                />
                            </svg>
                            {upgradingMission.repeatable && (
                                <Box
                                    sx={{
                                        transform: 'translate(-50%, -45%)',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        width: '40%',
                                        height: '40%',
                                        zIndex: '100',
                                    }}
                                >
                                    <CounterOverlay value={(upgradingMission.counter || 1) - 1} animated />
                                </Box>
                            )}
                        </>
                    )}

                    {(upgradeStep === 'upgrading' || upgradeStep === 'upgraded') && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                zIndex: 10000,
                            }}
                        >
                            <DotLottieReact
                                dotLottieRefCallback={upgradeArrowDotLottieRefCallback}
                                src="/assets/animations/rewards/achievement-upgrade-arrow.lottie"
                                autoplay
                                loop={false}
                                layout={{ fit: 'contain', align: [0, 0] }}
                                renderConfig={{
                                    autoResize: true,
                                }}
                            />
                        </Box>
                    )}

                    {upgradeStep === 'upgraded' && achievement && (
                        <>
                            {/* Achievement upgraded animation */}
                            <DotLottieReact
                                dotLottieRefCallback={upgradedDotLottieRefCallback}
                                src="/assets/animations/rewards/achievement-upgraded.lottie"
                                autoplay
                                layout={{ fit: 'contain', align: [0, 0] }}
                                renderConfig={{
                                    autoResize: true,
                                }}
                            />

                            <>
                                <svg
                                    width="100%"
                                    height="100%"
                                    viewBox="0 0 557 572"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    preserveAspectRatio="xMidYMid meet"
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        zIndex: 1,
                                        transform: showReward ? 'scale(1)' : 'scale(0)',
                                        transition: 'transform 0.3s ease-in-out',
                                        animation: 'upgraded 1s ease-in-out forwards',
                                    }}
                                >
                                    <image
                                        href={achievement.assetUrl}
                                        height="220"
                                        width="220"
                                        x={(557 - 220) / 2}
                                        y="190"
                                    />
                                </svg>
                                {achievement.repeatable && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: `translate(-50%, -45%)`,
                                            width: showReward ? '50%' : '0%',
                                            height: showReward ? '50%' : '0%',
                                            transition: 'width 0.3s ease-in-out, height 0.3s ease-in-out',
                                            zIndex: '10',
                                        }}
                                    >
                                        <CounterOverlay value={achievement.counter || 1} animated />
                                    </Box>
                                )}
                            </>
                        </>
                    )}
                </Box>
            </Box>
        )
    }

    // Regular flow (non-tier upgrade)
    if (!achievement) return null

    const { assetUrl, counter } = achievement
    const isUpgraded = false // DEV-7382

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9999,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    maxWidth: '600px',
                    width: '100%',
                    aspectRatio: '1/1',
                    filter: 'drop-shadow(4px 10px 4px rgba(0, 0, 0, 0.75))',
                    position: 'relative',
                    'svg, #sparkles': {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                    },
                    svg: {
                        zIndex: 1,
                        '@keyframes earned': {
                            '0%': { opacity: 0, transform: 'scale(0)' },
                            '100%': { opacity: 1, transform: 'scale(1)' },
                        },
                        '@keyframes upgraded': {
                            '0%': { opacity: 0, transform: 'scale(3)' },
                            '100%': { opacity: 1, transform: 'scale(1)' },
                        },
                        animation: `${isUpgraded ? 'upgraded' : 'earned'} 1s ease-in-out forwards`,
                    },
                    '#sparkles': {
                        zIndex: 2,
                    },
                }}
            >
                <DotLottieReact
                    dotLottieRefCallback={dotLottieRefCallback}
                    src={`/assets/animations/rewards/achievement-${isUpgraded ? 'upgraded' : 'earned'}.lottie`}
                    autoplay
                    layout={{ fit: 'contain', align: [0, 0] }}
                    renderConfig={{
                        autoResize: true,
                    }}
                />
                {showReward && (
                    <>
                        <Box id="sparkles">
                            <DotLottieReact
                                src="/assets/animations/rewards/sparkles.lottie"
                                autoplay
                                layout={{ fit: 'contain', align: [0, 0] }}
                                renderConfig={{
                                    autoResize: true,
                                }}
                            />
                        </Box>
                        <svg
                            width="100%"
                            height="100%"
                            viewBox="0 0 557 572"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            preserveAspectRatio="xMidYMid meet"
                        >
                            <image href={assetUrl} height="220" width="220" x={(557 - 220) / 2} y="190" />
                        </svg>
                        {achievement.repeatable && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -45%)',
                                    width: '50%',
                                    height: '50%',
                                    zIndex: '10',
                                }}
                            >
                                <CounterOverlay value={counter || 1} />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    )
}
